import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { parseAdsFeedSummary, type AdsFeedSummaryRow, type ParsedAdsFeedSummary } from '@/lib/adsFeedSummary';

export const ADS_FEED_SUMMARY_QUERY_KEY = 'ads-feed-summary' as const;
export const ADS_FEED_SUMMARY_POLL_MS = 10_000;

/**
 * Kapitel-3-Cache ueber das PAAR (Feed, Ads-Konto), nie ueber feed_id allein:
 * ein Feed kann Zeilen zweier Ads-Konten tragen (Spec 2), und maybeSingle()
 * antwortete dann mit PGRST116 genau bei dem Kunden, fuer den der zweiteilige
 * Schluessel gebaut ist.
 *
 * `pollUntil` (ms-Timestamp) treibt das Polling im Zustand "noch keine Zahlen"
 * nach dem Connect: alle 10 s, bis ein Eintrag da ist oder die Frist verstrichen.
 */
export function useAdsFeedSummary(
  feedId: string | null,
  adsAccountId: string | null,
  opts: { pollUntil?: number | null } = {},
) {
  return useQuery({
    queryKey: [ADS_FEED_SUMMARY_QUERY_KEY, feedId, adsAccountId],
    enabled: !!feedId && !!adsAccountId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      if (query.state.data) return false;
      return opts.pollUntil && Date.now() < opts.pollUntil ? ADS_FEED_SUMMARY_POLL_MS : false;
    },
    queryFn: async (): Promise<ParsedAdsFeedSummary | null> => {
      const { data, error } = await supabase
        .from('ads_feed_summaries')
        .select('feed_id, ads_account_id, window_start, window_end, data_through, currency, summary, computed_at')
        .eq('feed_id', feedId as string)
        .eq('ads_account_id', adsAccountId as string)
        .maybeSingle();
      if (error) {
        Sentry.captureException(error, { tags: { feature: 'ads-chapter', hook: 'useAdsFeedSummary' } });
        throw error;
      }
      return data ? parseAdsFeedSummary(data as AdsFeedSummaryRow) : null;
    },
  });
}
